import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDidUpdate } from 'rooks';
import { useLocation, useNavigate } from 'react-router-dom';
import pluralize from 'pluralize';
import { Col, Row, Spin } from 'antd';
import queryString from 'query-string';
import InfiniteScroll from 'react-infinite-scroller';

import {
  getApprovedWorkers,
  getWorkersForCurrentMember,
  handleWorkerModalOpen,
} from 'redux/reducers/workerSlice';
import WorkerCard from '../WorkerCard';
import { WorkerItemDetailViewModel } from 'api/adminApiClient';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { workerCreatedAtSelector } from 'redux/selectors/workerSelectors';
import BaseSearch from 'components/wrappers/BaseSearch';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

interface WorkerQueryType {
  query: string;
  section: string;
}
export default function WorkersSection() {
  const dispatch = useAppDispatch();
  const latestRequestId = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { query, section } = queryString.parse(location.search);
  const workerCreatedAt = useAppSelector(workerCreatedAtSelector);
  const [workers, setWorkers] = useState<WorkerItemDetailViewModel[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false);
  const isAdmin = useAppSelector(isAdminSelector);
  const infiniteScrollParentRef = useRef(null);

  useDidUpdate(() => {
    const queryData: WorkerQueryType = {
      query,
      section,
    };
    navigate(`${location.pathname}?${queryString.stringify(queryData)}`, {
      replace: true,
    });
  }, [query]);

  useEffect(() => {
    initInfinityScroll();
  }, [workerCreatedAt, query]);

  const initInfinityScroll = () => {
    setIsVisible(false);
    setWorkers([]);
    setIsInitialDataLoaded(false);
    setHasMore(true);
    setTotalCount(0);
    const timer = setTimeout(() => {
      // to destroy InfiniteScroll
      setIsVisible(true);
    }, 0);

    return () => clearTimeout(timer);
  };

  const onSetWorkers = useCallback(
    (result) => {
      const loadedWorkers = result.payload.details || [];
      const totalCount = result.payload.totalCount || 0;
      const newWorkers = [...workers, ...loadedWorkers];
      const hasMoreRows = newWorkers.length < totalCount;

      setHasMore(hasMoreRows);
      setWorkers(newWorkers);
      setTotalCount(totalCount);
    },
    [workers],
  );

  const onLoadMore = useCallback(
    async (pageNumber: number) => {
      const requestId = Date.now();
      latestRequestId.current = requestId;

      const correctAction = isAdmin
        ? getApprovedWorkers
        : getWorkersForCurrentMember;

      const result = await dispatch(
        correctAction({
          pageNumber,
          searchQuery: query ? query.trim() : undefined,
          requestId,
        }),
      );
      if (result.meta.arg.requestId === latestRequestId.current) {
        if (correctAction.fulfilled.match(result)) {
          onSetWorkers(result);
        } else {
          setHasMore(false);
        }

        setIsInitialDataLoaded(true);
      }
    },
    [dispatch, onSetWorkers, isAdmin, query],
  );

  const getNotFoundMessage = () => {
    const handleCreate = (event: any) => {
      event.preventDefault();
      dispatch(handleWorkerModalOpen(true));
    };

    return isAdmin ? (
      'There are no unique workers.'
    ) : (
      <div>
        You have no workers. Perhaps it is time to{' '}
        <a href="/" onClick={handleCreate}>
          create
        </a>{' '}
        a new one? Let us know if we can help.
      </div>
    );
  };

  const getTotalCountLabel = () => {
    return isAdmin
      ? `${pluralize('unique worker', totalCount, true)} 
         (${pluralize('non-duplicate', totalCount)})`
      : pluralize('worker', totalCount, true);
  };

  return (
    <div ref={infiniteScrollParentRef} className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <Row gutter={[20, 10]}>
            <Col xs={24} md={6}>
              <BaseSearch
                searchQuery={query}
                placeholder="Search by worker name, ID"
              />
            </Col>
            <Col xs={24}>
              {isInitialDataLoaded && (
                <>
                  {!!totalCount && (
                    <p className="page-total-count">{getTotalCountLabel()}</p>
                  )}

                  {!totalCount && (
                    <span className="no-data-msg">{getNotFoundMessage()}</span>
                  )}
                </>
              )}
            </Col>
          </Row>
          {isVisible && (
            <InfiniteScroll
              pageStart={-1}
              loadMore={onLoadMore}
              hasMore={hasMore}
              loader={<Spin key={0} />}
              useWindow={false}
              getScrollParent={() => infiniteScrollParentRef.current}
            >
              <Row gutter={[20, 20]}>
                {workers.map((worker, index) => (
                  <Col
                    md={24}
                    lg={12}
                    className="d-flex"
                    key={`${worker.id}-${index}`}
                  >
                    <WorkerCard worker={worker} />
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
